import { useIsFetching } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { AgentPanel, conversationKeys, usePointTo } from "../../../agent";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import type { Screen } from "../../../api/types";
import { AccountsSection } from "./AccountsSection";
import { DisciplinesSection } from "./DisciplinesSection";
import { ModelConnectionSection } from "./ModelConnectionSection";

type SectionId = "accounts" | "model-connection" | "disciplines";

const SECTION_OF_SCREEN: Partial<Record<Screen, SectionId>> = {
  admin_accounts: "accounts",
  admin_model_connection: "model-connection",
  admin_disciplines: "disciplines",
};

const POINT_MARK_MS = 10_000;

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView?.({ block: "start" });
}

/**
 * The admin's page (ui-direction screen 10): accounts with roles, the model connection, and the disciplines, with the
 * admin's own agent beside them. The admin sees no ontology work and no one's memory (architecture 15.1).
 */
export function AdminPage() {
  const me = useCurrentMe();
  const { hash } = useLocation();
  const [pointed, setPointed] = useState<SectionId | null>(null);
  // The helper's panel opens when it is wanted, so the page does not jump to it when it loads.
  const [helperOpen, setHelperOpen] = useState(false);

  // An address with #accounts, #model-connection or #disciplines (the agent's pointers use them) opens at that part, once
  // the lists above it have been read (they change how tall the page is).
  const reading = useIsFetching() > 0;
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!hash || reading || scrolledTo.current === hash) return;
    scrolledTo.current = hash;
    scrollToSection(hash.slice(1));
  }, [hash, reading]);

  usePointTo(
    (event) => {
      const section = SECTION_OF_SCREEN[event.screen];
      if (section) setPointed(section);
    },
    { screens: ["admin_accounts", "admin_model_connection", "admin_disciplines"] },
  );
  useEffect(() => {
    if (!pointed) return;
    scrollToSection(pointed);
    const timer = setTimeout(() => setPointed(null), POINT_MARK_MS);
    return () => clearTimeout(timer);
  }, [pointed]);

  return (
    <div className="ra-page stack">
      <header className="ra-page__head">
        <h1>Admin</h1>
        <p className="muted">Accounts, the model connection and the disciplines. The admin account stays apart from the Engineer and SME roles and sees none of their work.</p>
        <nav aria-label="Parts of this page" className="row ra-admin__nav">
          <a href="#accounts">Accounts</a>
          <a href="#model-connection">Model connection</a>
          <a href="#disciplines">Disciplines</a>
          <Link to="/change-password" state={{ from: "/admin" }}>
            Change my password
          </Link>
        </nav>
      </header>

      <AccountsSection pointed={pointed === "accounts"} />
      <ModelConnectionSection pointed={pointed === "model-connection"} />
      <DisciplinesSection pointed={pointed === "disciplines"} />

      <section aria-label="The admin's agent" className="ra-helper">
        {helperOpen ? (
          <>
            <AgentPanel
              conversationKey={conversationKeys.admin(me.person.id)}
              mode="admin"
              title="Admin helper"
              context={{ screen: "admin_accounts" }}
              emptyHint="Ask how to add an account, set the model connection or close a discipline."
            />
            <Button variant="quiet" onClick={() => setHelperOpen(false)}>
              Hide the admin helper
            </Button>
          </>
        ) : (
          <Button onClick={() => setHelperOpen(true)}>Ask the admin helper</Button>
        )}
      </section>
    </div>
  );
}
